import React from 'react';

import BlockContent, {BlockContentProps} from '@sanity/block-content-to-react';
import {SponsoredArticleBannerInline} from 'src/components/editorial/SponsoredArticleBannerInline';
import {LabelledImage, EditorialContent} from 'src/components/shared';
import {ResponsiveContentWrapper} from 'src/components/shared/ResponsiveContentWrapper';
import {Typography} from 'src/components/shared/typography';
import {colors} from 'src/styleguide/colors';
import {fonts} from 'src/styleguide/typographyStyles';
import styled from 'styled-components/macro';

import {BodyCallOutText, Factoid, EmailBanner} from '../../components/editorial';
import {FloatedImage} from './FloatedImage';
import {EditorialGallery} from './Gallery';
import {PullQuoteImage} from './PullQuoteImage';
import {EditorialQuote} from './Quote';

export type RichContentType = typeof BlockContent;

const primaryTextAttributes = {
  mb: '2rem',
  fontSize: '1.125rem',
  lineHeight: '1.5rem',
  fontFamily: fonts.primary,
  color: colors.washingtonPostFontGray,
};

const EditorialWistiaVideo = styled('iframe')`
  width: 100%;
  height: 100%;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ListComponent: any = (props: any) =>
  props.type === 'number' ? <ol>{props.children}</ol> : <ul>{props.children}</ul>;

type BlockComponentStyles = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'normal' | 'blockquote';
export type SerializerProps = {
  children: JSX.Element;
  node: {
    style: BlockComponentStyles;
  };
};

const serializers: BlockContentProps['serializers'] = {
  marks: {
    link: ({mark, children}) => (
      <a href={mark.href} target="_blank" rel="noreferrer noopener">
        {children}
      </a>
    ),
  },
  list: ListComponent,
  types: {
    quote: ({node}) => {
      return <EditorialQuote {...node} />;
    },
    bodyCallOutText: ({node}) => {
      return <BodyCallOutText {...node} />;
    },
    labelledImage: ({node}) => {
      return <LabelledImage {...node} />;
    },
    wistiaVideo: ({node}) => (
      <div>
        <ResponsiveContentWrapper aspectRatio="16:9">
          <EditorialWistiaVideo src={`https://fast.wistia.net/embed/iframe/${node.wistiaVideoId}`} />
        </ResponsiveContentWrapper>
        <div className="col-span-full mb-8 mt-2">
          <p className="type-xs-p text-right videoAttribution">{node.attribution}</p>
        </div>
      </div>
    ),
    block: ({children, node}) => {
      switch (node.style) {
        case 'heading1':
          return <h1>{children}</h1>;
        case 'heading2':
          return <h2>{children}</h2>;
        case 'heading3':
          return <h3>{children}</h3>;
        case 'heading4':
          return <h4>{children}</h4>;
        default:
          return <p>{children}</p>;
      }
    },
    imagesGallery: ({node}) => <EditorialGallery gallery={node} />,
    factoid: ({node}) => {
      return <Factoid {...node} />;
    },
    emailSignUp: ({node}) => {
      return <EmailBanner id="newsletter-form" {...node} />;
    },
    floatedImage: ({node}) => {
      return <FloatedImage {...node} />;
    },
    pullQuoteImage: ({node}) => {
      return <PullQuoteImage {...node} />;
    },
    sponsoredArticle: ({node}) => {
      return <SponsoredArticleBannerInline {...node} />;
    },
  },
};

export const EditorialRichContent: RichContentType = props => (
  <EditorialContent serializers={serializers} blocks={props.blocks} className="text-gray prose articleBody" />
);
